import { EchoFilterRequest, FilterKeyToPropertyMap } from '../filter/types';
import type { ExpressionKey } from '@/js/consts/language/en';

export type IPaginationSettings = {
    page: number;
    pageSize: number;
    sortOption?: ISortOption;
};

export type RequestGetInteractions = {
    ids: string[];
};

export type ResponseGetInteractions = {
    interactions: IInteraction[];
};

export type RequestInteractionTable = {
    filter: EchoFilterRequest;
} & IPaginationSettings;

export type RequestUpdateTableLayout = {
    columns: Pick<ResponseColumnHeader, 'id' | 'order'>[];
    tableKey: 'Interactions';
};

export type ISortOption = {
    columnId: string;
    sortDirection: ISortDirection;
};

export type ISortDirection = 'Asc' | 'Desc';

export type IPagination = {
    page: number;
    pageSize: number;
    totalRows: number;
};

export type ResponseInteractions = {
    columnHeaders: ResponseColumnHeader[];
    rowHeaders: ResponseRowHeader[];
    rowCells: ResponseRowCell[];
    pagination: IPagination;
};

export type ResponseColumnHeader = {
    id: string;
    expandsFromParentId: string | null;
    translationKey: ExpressionKey;
    type: string;
    sortable: boolean;
    order: number | null;
    filterKey: keyof FilterKeyToPropertyMap;
    dynamicFilterKeyName?: string;
    color?: string;
};

export enum ChannelName {
    CALL = 'Call',
    CALLBACK = 'Callback',
    OUTGOING_CALL = 'Outgoing call',
    MAIL = 'Mail',
    CHAT = 'Chat',
    NO_INTERACTION = 'No interaction',
}

export enum ChannelIcon {
    HEART = 0,
    HEADPHONES = 1,
    TEXT = 2,
    MAIL = 3,
    CHAT = 4,
}

export type ResponseRowHeader = {
    externalId: string;
    id: string;
    agentName: string;
    timestamp: Date;
    channelType: number;
    channel: ChannelName;
    channelIcon?: ChannelIcon;
    index: number;
};

export type ResponseRowCell = {
    columnId: string;
    value: string | string[];
    rowIndex: number;
};

export type ITableInteraction = {
    columnHeaders: ResponseColumnHeader[];
    rowCells: IRowCell[];
    pagination: IPagination;
};

export type IRowCell = ResponseRowHeader & {
    sentiment: string[];
    ['agent-sentiment']: string[];
    ['customer-sentiment']: string[];
    ['agent-sentiment-level-names']: string[];
    ['customer-sentiment-level-names']: string[];
    ['agent-sentiment-score-percentage']: string | number;
    ['customer-sentiment-score-percentage']: string | number;
    ['tracker-names']: string[];
    ['skill-names']: string[];
    ['agent-direction']: string;
    ['customer-direction']: string;
    ['agent-sentiment-direction']: string;
    ['customer-sentiment-direction']: string;
    ['main-category']: string;
    ['sub-category']: string;
    queue: string;
};

export type IInteraction = {
    id: string;
    externalId: string;
    channelType: 0 | 1;
    channelName: ChannelName;
    channelIcon: ChannelIcon;
    startTime: string;
    skillName: string | null;
    agentName: string;
    summary: IInteractionSummary | null;
    transcription: IInteractionTranscription;
};

export type IInteractionSummary = {
    trackers: string[];
    categories: string[];
    audioFileLength: number;
    summary: string;
    reasonForContact: string | null;
    solution: string | null;
    solved: string | null;
    sentimentScore: number;
    sentimentLevel: string;
    sentimentDirection: string;
};

export type IInteractionTranscription = IInteractionTranscriptionSegment[];

export type IInteractionTranscriptionSegment = {
    segmentId: string;
    speaker: string;
    text: string;
    segmentSentiment: string;
    segmentStart: number;
    segmentEnd: number;
};

export enum StreamingAvailability {
    Available = 0,
    Expired = 1,
    Anonymized = 2,
}

export type InteractionAudioMetaData = {
    audioFileLengthSeconds: number;
    audioPlaybackUrl: string;
    streamingAvailability: StreamingAvailability;
};
